import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OU_PALETTE, getOUColor } from '../data';
import { useBuilderContext } from '../context/BuilderContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserContext } from '@/contexts/UserContext';
import { useOUs, useOUItems } from '@/hooks/useOUs';
import { OuCascadeSelect, buildOuTree, collectDescendantItems } from '@/components/common/OuTree';
import { InitialsAvatar } from '@/components/common';
import { useAudiencePeople } from '@/hooks/useUsers';
import { useDebounce } from '@/hooks/useDebounce';

const DEST_PAGE_SIZE = 10;

// Se define a nivel de módulo, nunca dentro del componente que lo usa (regla de ESLint
// `react/no-unstable-nested-components`).
function PersonAvatar({ name, colorIndex }: Readonly<{ name: string; colorIndex: number }>) {
  return <InitialsAvatar name={name} baseClass="dest-chip-av" colorIndex={colorIndex} />;
}

export function StepAudiencia() {
  const { t } = useTranslation();
  const { form, setForm } = useBuilderContext();
  const currentUser = useCurrentUser();
  const { demoUser } = useUserContext();
  // El acceso se decide con la lista completa de roles, nunca con el rol de etiqueta.
  const effectiveRoles = currentUser?.roles ?? [demoUser.role];
  const isGlobalAdmin = effectiveRoles.includes('AdminGlobal');
  const [expandedRootKey, setExpandedRootKey] = useState<string | null>(null);
  const [ouPage, setOuPage] = useState(1);

  const [peopleSearch, setPeopleSearch] = useState('');
  const debouncedPeopleSearch = useDebounce(peopleSearch);

  const { data: peopleData, isLoading: isPeopleLoading, isError: isPeopleError } =
    useAudiencePeople(form.destOU, debouncedPeopleSearch, 1, 50);
  const { data: ouPeopleData, isLoading: isOuPeopleLoading, isError: isOuPeopleError } =
    useAudiencePeople(form.destOU, '', ouPage, DEST_PAGE_SIZE);

  const { data: allOUs = [] } = useOUs();
  const { data: allOuItems = [] } = useOUItems();
  // GlobalAdmin sees all OUs. AdminOU users will be scoped to their assigned OU(s)
  // once backend permission filtering is enabled with real auth.
  // Solo nivel 1 (raíces del árbol): useOUs() devuelve el catálogo activo plano,
  // sin distinguir jerarquía, así que con datos reales (BUs anidadas bajo una OU)
  // listaba todo como si fuera nivel 1. buildOuTree ya resuelve esa jerarquía.
  const rootOuItems = useMemo(() => buildOuTree(allOuItems).map(node => node.ou), [allOuItems]);
  const visibleOUs = rootOuItems;
  const mainOUs   = visibleOUs.filter(o => !o.gdpr);
  const europaOUs = visibleOUs.filter(o => o.gdpr);

  // Color por orden de paleta (no por hash del id): así dos OUs adyacentes en la
  // grilla nunca comparten color, a diferencia de getOUColor (hash), que puede
  // asignar el mismo color de la paleta a ids consecutivos.
  const ouColorById = useMemo(() => {
    const colorMap = new Map<string, string>();
    mainOUs.forEach((ouItem, ouIndex) => colorMap.set(ouItem.id, OU_PALETTE[ouIndex % OU_PALETTE.length]!));
    return colorMap;
  }, [mainOUs]);

  function getCardColor(ouId: string): string {
    return ouColorById.get(ouId) ?? getOUColor(ouId);
  }

  // ── Personalizada ──────────────────────────────────────────────────────────
  if (form.surveyType === 'personalizada') {
    const selectedPeople = form.destPeople;

    const available = (peopleData?.people ?? []).filter(p => !selectedPeople.includes(p.id));

    const ouPagedPeople = ouPeopleData?.people ?? [];
    const ouTotalCount = ouPeopleData?.totalCount ?? 0;
    const ouPageCount = Math.ceil(ouTotalCount / DEST_PAGE_SIZE);

    const addPerson    = (id: string) => setForm(p => ({ ...p, destPeople: [...p.destPeople, id] }));
    const removePerson = (id: string) => setForm(p => ({ ...p, destPeople: p.destPeople.filter(x => x !== id) }));

    return (
      <div className="builder-section">
        <h3><i className="fas fa-users" /> {t('admin.builderAudienceCustomTitle')}</h3>
        <p className="cfg-section-sub" style={{ marginBottom: 16 }}>{t('admin.builderAudienceCustomSubtitle')}</p>
        <div className="form-group">
          <label>{t('admin.builderRecipientsLabel')} *</label>
          <div className="dest-mode-tabs">
            {(['meeting', 'people', 'ou'] as const).map(m => (
              <button
                key={m}
                className={`dest-tab${form.destMode === m ? ' active' : ''}`}
                onClick={() => setForm(p => ({ ...p, destMode: m, destOU: '', destPeople: [] }))}
              >
                <i className={`fas fa-${m === 'meeting' ? 'video' : m === 'people' ? 'user-check' : 'sitemap'}`} />
                {m === 'meeting'
                  ? ` ${t('admin.builderByMeetingId')}`
                  : m === 'people'
                  ? ` ${t('admin.builderByPeople')}`
                  : ` ${t('admin.builderByOU')}`}
              </button>
            ))}
          </div>

          {/* ── Meeting ID ── */}
          {form.destMode === 'meeting' && (
            <>
              <input
                type="text"
                className="form-input"
                style={{ marginTop: 10 }}
                value={form.sessionId}
                placeholder={t('admin.builderMeetingIdPlaceholder')}
                onChange={e => setForm(p => ({ ...p, sessionId: e.target.value }))}
              />
              <small className="form-hint">{t('admin.builderMeetingIdHint')}</small>
            </>
          )}

          {/* ── Personas específicas ── */}
          {form.destMode === 'people' && (
            <div className="dest-people-wrap">
              {selectedPeople.length > 0 ? (
                <div className="dest-chips">
                  {selectedPeople.map((personaId, posicion) => {
                    const persona = peopleData?.people.find(x => x.id === personaId);
                    if (!persona) return null;
                    return (
                      <span key={personaId} className="dest-chip">
                        <PersonAvatar name={persona.name} colorIndex={posicion} />
                        {persona.name}
                        <button className="btn btn--secondary" onClick={() => removePerson(personaId)} title={t('admin.builderRemovePerson')}>
                          <i className="fas fa-times" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="dest-empty-hint">
                  <i className="fas fa-user-plus" /> {t('admin.builderSelectPeople')}
                </p>
              )}
              <div className="dest-filter-row">
                <select
                  className="form-input dest-ou-filter"
                  value={form.destOU}
                  onChange={e => setForm(p => ({ ...p, destOU: e.target.value }))}
                >
                  <option value="">{t('admin.builderAllOUs')}</option>
                  {allOUs.map(ou => <option key={ou.key} value={ou.key}>{ou.label}</option>)}
                </select>
              </div>
              <input
                type="text"
                className="form-input"
                style={{ marginBottom: 8 }}
                value={peopleSearch}
                placeholder={t('admin.builderSearchPeoplePlaceholder')}
                onChange={e => setPeopleSearch(e.target.value)}
              />
              <div className="dest-people-list">
                {isPeopleLoading ? (
                  <p className="dest-empty-hint">{t('admin.builderLoadingPeople')}</p>
                ) : isPeopleError ? (
                  <p className="dest-empty-hint">{t('admin.builderErrorLoadingPeople')}</p>
                ) : available.length > 0 ? available.map((persona, posicion) => (
                  <div key={persona.id} className="dest-person-row" onClick={() => addPerson(persona.id)}>
                    <PersonAvatar name={persona.name} colorIndex={posicion} />
                    <div className="dest-person-info">
                      <span className="dest-person-name">{persona.name}</span>
                      <span className="dest-person-role">{persona.jobTitle}</span>
                    </div>
                    <i className="fas fa-plus dest-person-add" />
                  </div>
                )) : (
                  <p className="dest-empty-hint">{t('admin.builderAllAvailableAdded')}</p>
                )}
              </div>
              <small className="form-hint">
                {selectedPeople.length}{' '}
                {selectedPeople.length !== 1
                  ? t('admin.builderPeopleSelected')
                  : t('admin.builderPersonSelected')}
              </small>
            </div>
          )}

          {/* ── Por organigrama (OU) ── */}
          {form.destMode === 'ou' && (
            <div className="dest-ou-wrap">
              <select
                className="form-input"
                value={form.destOU}
                onChange={e => { setForm(p => ({ ...p, destOU: e.target.value })); setOuPage(1); }}
              >
                <option value="">{t('admin.builderSelectOU')}</option>
                {allOUs.map(ou => (
                  <option key={ou.key} value={ou.key}>{ou.label}</option>
                ))}
              </select>

              {form.destOU && isOuPeopleLoading && (
                <p className="dest-empty-hint" style={{ marginTop: 12 }}>{t('admin.builderLoadingPeople')}</p>
              )}

              {form.destOU && isOuPeopleError && (
                <p className="dest-empty-hint" style={{ marginTop: 12 }}>{t('admin.builderErrorLoadingPeople')}</p>
              )}

              {form.destOU && !isOuPeopleLoading && !isOuPeopleError && ouTotalCount > 0 && (
                <>
                  <div className="dest-ou-preview">
                    {ouPagedPeople.map((persona, posicion) => (
                      <div key={persona.id} className="dest-ou-person">
                        <InitialsAvatar name={persona.name} baseClass="dest-chip-av small" colorIndex={posicion} />
                        <span className="dest-ou-person-name">{persona.name}</span>
                        <span className="dest-ou-role">{persona.jobTitle}</span>
                      </div>
                    ))}
                  </div>

                  {ouPageCount > 1 && (
                    <div className="dest-ou-pager">
                      <button className="btn btn--secondary" disabled={ouPage === 1} onClick={() => setOuPage(ouPage - 1)}>
                        <i className="fas fa-chevron-left" />
                      </button>
                      <span>{ouPage} / {ouPageCount}</span>
                      <button className="btn btn--secondary" disabled={ouPage === ouPageCount} onClick={() => setOuPage(ouPage + 1)}>
                        <i className="fas fa-chevron-right" />
                      </button>
                    </div>
                  )}

                  <div className="dest-ou-summary">
                    <i className="fas fa-info-circle" />
                    <span>
                      <strong>{ouTotalCount} {ouTotalCount !== 1 ? t('admin.builderPersonsPlural') : t('admin.builderPersonSingular')}</strong>
                      {' '}{t('admin.builderFromOU')}{' '}
                      <strong>{form.destOU}</strong>{' '}
                      {t('admin.builderWillReceiveSurvey')}
                    </span>
                  </div>
                </>
              )}

              {!form.destOU && (
                <p className="dest-empty-hint" style={{ marginTop: 12 }}>
                  <i className="fas fa-sitemap" /> {t('admin.builderSelectOUForTeam')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── General audience (OU cascader) ──────────────────────────────────────────
  const selected = form.audienceOUs;
  const isGlobal = selected.length === 0;
  const isEuropaOnly = europaOUs.length > 0 && selected.length === 1 && selected[0] === europaOUs[0]!.id;

  /** Selecciona una OU raíz (la agrega a audienceOUs) y abre su panel de cascada. */
  function selectOU(key: string) {
    setExpandedRootKey(key);
    setForm(p => {
      const ous = p.audienceOUs.filter(k => k !== 'global' && !europaOUs.some(e => e.id === k));
      if (!ous.includes(key)) ous.push(key);
      return {
        ...p,
        audienceOUs: ous,
        audienceSubOUs: { ...p.audienceSubOUs, [key]: p.audienceSubOUs[key] ?? [] },
      };
    });
  }

  /**
   * Quita una OU raíz por completo de la selección (no solo su sub-selección):
   * un `audienceSubOUs[key]` vacío significa "sin personalizar = toda la OU"
   * (ver deriveOuIds), así que la única forma real de deseleccionarla es
   * sacar `key` de `audienceOUs`. También colapsa su panel si estaba abierto.
   */
  function deselectOU(key: string) {
    setForm(p => {
      const { [key]: _removedSubSelection, ...restSubOUs } = p.audienceSubOUs;
      return {
        ...p,
        audienceOUs: p.audienceOUs.filter(k => k !== key),
        audienceSubOUs: restSubOUs,
      };
    });
    setExpandedRootKey(current => (current === key ? null : current));
  }

  /**
   * Clic en una tarjeta de OU:
   * - No seleccionada           → la selecciona y abre su panel.
   * - Seleccionada y colapsada  → reabre su panel (para revisar/editar), sin tocar la selección.
   * - Seleccionada y expandida  → la quita por completo de la selección y colapsa el panel.
   */
  function toggleOU(key: string) {
    if (key === 'global') {
      setForm(p => ({ ...p, audienceOUs: [], audienceSubOUs: {} }));
      setExpandedRootKey(null);
    } else if (europaOUs.some(o => o.id === key)) {
      if (selected.includes(key)) {
        setForm(p => ({ ...p, audienceOUs: [] }));
      } else {
        setForm(p => ({ ...p, audienceOUs: [key], audienceSubOUs: {} }));
      }
      setExpandedRootKey(null);
    } else if (expandedRootKey === key) {
      deselectOU(key);
    } else if (selected.includes(key)) {
      setExpandedRootKey(key);
    } else {
      selectOU(key);
    }
  }

  /** Cierra el panel de cascada (vuelve a la grilla de nivel 1) sin tocar la selección. */
  function collapseCascadePanel() {
    setExpandedRootKey(null);
  }

  const expandedRootItems = expandedRootKey ? collectDescendantItems(allOuItems, expandedRootKey) : [];
  const expandedRootOu = expandedRootKey ? allOuItems.find(item => item.id === expandedRootKey) : undefined;

  const cascadePanel = expandedRootKey && expandedRootOu ? (
    <OuCascadeSelect
      key={expandedRootKey}
      rootOu={expandedRootOu}
      items={expandedRootItems}
      value={form.audienceSubOUs[expandedRootKey] ?? []}
      onChange={subIds => setForm(p => ({ ...p, audienceSubOUs: { ...p.audienceSubOUs, [expandedRootKey]: subIds } }))}
      color={getCardColor(expandedRootKey)}
      onCollapse={collapseCascadePanel}
      onRootDeselect={() => deselectOU(expandedRootKey)}
    />
  ) : null;

  return (
    <div className="builder-section">
      <h3><i className="fas fa-users" /> {t('admin.builderAudienceSectionTitle')}</h3>
      {isGlobalAdmin && (
        <div className="info-banner-small" style={{ marginBottom: 12 }}>
          <i className="fas fa-globe" style={{ color: 'var(--cp-purple)', flexShrink: 0 }} />
          <p>{t('admin.builderGlobalAdminOUAccess')}</p>
        </div>
      )}
      <div className="form-group">
        <label>
          {t('admin.builderWhoApplies')} *{' '}
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>
            {t('admin.builderSelectOneOrMoreOUs')}
          </span>
        </label>
        <div className="audience-cards">
          <div
            className={`audience-card${isGlobal ? ' selected' : ''}`}
            onClick={() => toggleOU('global')}
          >
            {isGlobal && <span className="au-check"><i className="fas fa-check" /></span>}
            <i className="fas fa-globe" style={{ color: 'var(--cp-purple)' }} />
            <strong>{t('admin.builderGlobal')}</strong>
            <span>{t('admin.builderGlobalDesc')}</span>
            <span className="au-europa-excl">
              <i className="fas fa-minus-circle" /> {t('admin.builderExcludesEurope')}
            </span>
          </div>
          {mainOUs.map(o => {
            const isSel = selected.includes(o.id);
            const subSel = form.audienceSubOUs[o.id] || [];
            const isFullSelectionViaSubOUs = subSel.length === 1 && subSel[0] === o.id;
            const partial = isSel && subSel.length > 0 && !isFullSelectionViaSubOUs;
            const isExp = expandedRootKey === o.id;
            return (
              <Fragment key={o.id}>
                <div
                  className={`audience-card${isSel ? ' selected' : ''}${isExp ? ' au-expanded' : ''}`}
                  onClick={() => toggleOU(o.id)}
                >
                  {partial
                    ? <span className="au-sub-count">{subSel.length}</span>
                    : isSel
                    ? <span className="au-check"><i className="fas fa-check" /></span>
                    : null
                  }
                  <i className="fas fa-building" style={{ color: getCardColor(o.id) }} />
                  <strong>{o.name}</strong>
                  <span>{o.type}</span>
                  <i className={`fas fa-chevron-right au-drill${isExp ? ' au-drill-open' : ''}`} />
                </div>
                {isExp && cascadePanel}
              </Fragment>
            );
          })}
        </div>
      </div>

      {europaOUs.length > 0 && (
        <div className="europa-ou-section">
          <div className="europa-ou-divider">
            <span className="europa-ou-label">
              <i className="fas fa-balance-scale" /> {t('admin.builderLegalRegulation')}
            </span>
          </div>
          <div className="audience-cards">
            {europaOUs.map(o => {
              const isSel = selected.includes(o.id);
              return (
                <div
                  key={o.id}
                  className={`audience-card audience-card--europa${isSel ? ' selected' : ''}`}
                  onClick={() => toggleOU(o.id)}
                >
                  {isSel && <span className="au-check"><i className="fas fa-check" /></span>}
                  <i className="fas fa-building" style={{ color: '#8DC63F' }} />
                  <strong>{o.name}</strong>
                  <span className="au-europa-tag">
                    <i className="fas fa-balance-scale" /> {t('admin.builderGDPRTag')}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="europa-excl-note">
            <i className="fas fa-info-circle" /> {t('admin.builderEuropeExcludedNote')}
          </p>
        </div>
      )}

      {isEuropaOnly && (
        <div className="info-banner-small" style={{ marginTop: 12, borderColor: 'rgba(141,198,63,0.3)', background: 'rgba(141,198,63,0.06)' }}>
          <i className="fas fa-info-circle" style={{ color: '#8DC63F', flexShrink: 0 }} />
          <p>{t('admin.builderEuropaAuthNote')}</p>
        </div>
      )}
    </div>
  );
}
